import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";

/** How wide the content runs beside the title: the column's width, a short table or set of facts, or a form's fields. */
const measures = { full: "", narrow: "max-w-4xl", form: "flex max-w-[480px] flex-col gap-5" } as const;

export type SectionProps = Omit<ComponentProps<"section">, "title"> & {
  /** The mono index, "01". Left out where the sections are not a sequence a person reads in order. */
  n?: string;
  title: ReactNode;
  /** A sentence or two under the title: what the section holds, or the rule its content follows. */
  note?: ReactNode;
  /** The title and note above the content instead of beside it, for content that needs the page's width (whole screens, wide tables). */
  wide?: boolean;
  measure?: keyof typeof measures;
};

/**
 * A numbered section of a settings, record or specimen page (the Swiss column): the mono index, the serif title and its
 * note in a 160px column from `md`, the content beside it; stacked on a phone. It draws its strong rule above itself,
 * none when it is the first of its container, so the boundary has one line. The title is isolated (`bdi`), so a name
 * a person typed in the other script keeps its own direction.
 */
export function Section({ n, title, note, wide = false, measure = "full", className, children, ...props }: SectionProps) {
  return (
    <section
      className={cn(
        "grid grid-cols-1 gap-6 border-t border-line-strong py-10 first:border-t-0",
        !wide && "md:grid-cols-[160px_minmax(0,1fr)] md:gap-8",
        className,
      )}
      {...props}
    >
      <div>
        {n && <p className="font-mono text-xs text-ink-3">{n}</p>}
        <h2 className={cn("font-display text-xl text-ink", n && "mt-1")}>
          <bdi>{title}</bdi>
        </h2>
        {note && (
          <p dir="auto" className={cn("mt-2 text-xs text-ink-3", wide && "max-w-prose")}>
            {note}
          </p>
        )}
      </div>
      <div className={cn("min-w-0", measures[measure])}>{children}</div>
    </section>
  );
}
